import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Trash2, XCircle, Crown, RotateCcw, Eye, EyeOff, MapPin, Copy, Ban, Download, ImageDown } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { useDataExport } from "@/hooks/useDataExport";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AvatarUpload } from "./AvatarUpload";
import { BlockedUsersList } from "./BlockedUsersList";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";


interface AccountSettingsProps {
  userId: string;
  isPremium?: boolean;
  isLoadingPremium?: boolean;
  showOnlyProfile?: boolean;
  showOnlyAccount?: boolean;
}

const AccountSettings = ({ userId, isPremium: isPremiumProp, isLoadingPremium, showOnlyProfile, showOnlyAccount }: AccountSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCanceling, setIsCanceling] = useState(false);
  const [isUncanceling, setIsUncanceling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [discordWebhook, setDiscordWebhook] = useState("");

  // Data export
  const { isExporting, isSavingPhotos, photoProgress, exportData, saveAllProgressPhotos } = useDataExport(userId);

  // Use centralized subscription status hook - this ensures subscription data
  // persists across pages and doesn't require refresh
  const {
    isPremium: isPremiumCentralized,
    isLifetime,
    subscriptionProductId,
    subscriptionEnd,
    isLoading: isLoadingSubscription
  } = useSubscriptionStatus(userId);

  // Use centralized premium status if not provided as prop
  const isPremium = isPremiumProp ?? isPremiumCentralized;

  const { data: profile, refetch: refetchProfile } = useQuery({
    queryKey: ["profile-settings", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_public, city, country, private_account_id, avatar_url, username, discord_webhook")
        .eq("id", userId)
        .single();
      if (error) throw error;

      // Set location state
      if (data) {
        setCity(data.city || "");
        setCountry(data.country || "");
        setDiscordWebhook(data.discord_webhook || "");
      }

      return data;
    },
  });

  // Check if subscription is scheduled to cancel
  // NOTE: Subscription status is now synced globally every minute by scheduled job
  // We read directly from the profile, but can call check-subscription once for cancel_at_period_end
  const { data: subscriptionStatus } = useQuery({
    queryKey: ["subscription-cancel-status", userId],
    queryFn: async () => {
      // Call check-subscription ONE TIME to get cancel_at_period_end status
      const { data: sessionData } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${sessionData.session?.access_token}` },
      });
      if (error) throw error;
      return data;
    },
    enabled: isPremium,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const updateVisibility = useMutation({
    mutationFn: async (isPublic: boolean) => {
      const { error } = await supabase
        .from("profiles")
        .update({ is_public: isPublic })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", userId] });
      toast.success(t('settings.profile.toast_visibility_updated'));
    },
    onError: () => {
      toast.error(t('settings.profile.toast_visibility_error'));
    },
  });

  const updateLocation = useMutation({
    mutationFn: async ({ city, country }: { city: string; country: string }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ city, country } as any)
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", userId] });
      toast.success(t('settings.profile.toast_location_updated'));
    },
    onError: () => {
      toast.error(t('settings.profile.toast_location_error'));
    },
  });

  const updateDiscordWebhook = useMutation({
    mutationFn: async (webhookUrl: string) => {
      const { error } = await supabase
        .from("profiles")
        .update({ discord_webhook: webhookUrl || null } as any)
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      queryClient.invalidateQueries({ queryKey: ["profile-settings", userId] });
      toast.success(t('settings.profile.toast_webhook_updated'));
    },
    onError: () => {
      toast.error(t('settings.profile.toast_webhook_error'));
    },
  });

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        headers: { Authorization: `Bearer ${sessionData.session?.access_token}` },
        body: { action: 'cancel' },
      });

      if (error) throw error;

      if (data?.success) {
        const dateText = data.cancel_at ? new Date(data.cancel_at).toLocaleDateString() : null;
        toast.success(dateText ? t('settings.account.toast_cancel_scheduled_date', { date: dateText }) : t('settings.account.toast_cancel_scheduled'));
        // Invalidate all subscription-related caches
        queryClient.invalidateQueries({ queryKey: ["subscription-status", userId] });
        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
        queryClient.invalidateQueries({ queryKey: ["profile-settings", userId] });
        queryClient.invalidateQueries({ queryKey: ["subscription-cancel-status", userId] });
        refetchProfile();
      } else {
        toast.error(data?.message || t('settings.account.toast_no_subscription'));
      }
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error(t('settings.account.toast_cancel_error'));
    } finally {
      setIsCanceling(false);
    }
  };

  const handleUncancelSubscription = async () => {
    setIsUncanceling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        headers: { Authorization: `Bearer ${sessionData.session?.access_token}` },
        body: { action: 'uncancel' },
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(t('settings.account.toast_reactivated'));
        // Invalidate all subscription-related caches
        queryClient.invalidateQueries({ queryKey: ["subscription-status", userId] });
        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
        queryClient.invalidateQueries({ queryKey: ["profile-settings", userId] });
        queryClient.invalidateQueries({ queryKey: ["subscription-cancel-status", userId] });
        refetchProfile();
      } else {
        toast.error(data?.message || t('settings.account.toast_reactivate_error'));
      }
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error(t('settings.account.toast_reactivate_error'));
    } finally {
      setIsUncanceling(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account");

      if (error) throw error;

      if (data?.success) {
        toast.success(t('settings.account.toast_deleted'));
        // Clear all localStorage including tutorial flags
        localStorage.clear();
        // The backend has already deleted the user, so we just clear local session
        // and navigate. Attempting to signOut() would fail with 403 since the token is invalid.
        await supabase.auth.signOut({ scope: 'local' });
        navigate("/auth");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(t('settings.account.toast_delete_error'));
      setIsDeleting(false);
    }
  };

  if (isLoadingPremium) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">{t('ui.text.loading_account_settings')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {showOnlyProfile && (
        <>
          <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
            <h3 className="text-lg font-semibold mb-4">{t('settings.profile.picture')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.profile.picture_desc')}
            </p>
            <AvatarUpload
              userId={userId}
              currentAvatarUrl={profile?.avatar_url}
              username={profile?.username}
              size="lg"
              showUploadButton={true}
            />
          </Card>

          <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
            <h3 className="text-lg font-semibold mb-4">{t('settings.profile.visibility')}</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {profile?.is_public
                    ? t('settings.profile.visibility_public')
                    : t('settings.profile.visibility_private')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="profile-visibility-settings"
                  checked={profile?.is_public ?? false}
                  onCheckedChange={(checked) => updateVisibility.mutate(checked)}
                  disabled={updateVisibility.isPending}
                />
                <Label htmlFor="profile-visibility-settings" className="text-sm cursor-pointer flex items-center gap-2">
                  {profile?.is_public ? (
                    <>
                      <Eye className="w-4 h-4" />
                      {t('settings.profile.public')}
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      {t('settings.profile.private')}
                    </>
                  )}
                </Label>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
            <h3 className="text-lg font-semibold mb-4">{t('settings.profile.location')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('settings.profile.location_desc')}
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="city">{t('settings.profile.city')}</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={t('settings.profile.city_placeholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t('settings.profile.country')}</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder={t('settings.profile.country_placeholder')}
                />
              </div>
              <Button
                onClick={() => updateLocation.mutate({ city, country })}
                disabled={updateLocation.isPending || !city || !country}
              >
                <MapPin className="w-4 h-4 mr-2" />
                {updateLocation.isPending ? t('common.saving') : t('settings.profile.save_location')}
              </Button>
            </div>
          </Card>

          <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
            <h3 className="text-lg font-semibold mb-4">{t('ui.text.discord_webhook_optional')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('ui.text.add_a_discord_webhook_url_to_automatically_share_y')}</p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="discord-webhook">{t('ui.text.discord_webhook_url')}</Label>
                <Input
                  id="discord-webhook"
                  type="url"
                  value={discordWebhook}
                  onChange={(e) => setDiscordWebhook(e.target.value)}
                  placeholder="https://discord.com/api/webhooks/..."
                />
                <p className="text-xs text-muted-foreground">{t('ui.text.get_your_webhook_url_from_discord_server_settings_')}</p>
              </div>
              <Button
                onClick={() => updateDiscordWebhook.mutate(discordWebhook)}
                disabled={updateDiscordWebhook.isPending}
              >
                {updateDiscordWebhook.isPending ? t('common.saving') : t('settings.profile.save_webhook')}
              </Button>
            </div>
          </Card>
        </>
      )}

      {!isPremium && !showOnlyProfile && (
        <Card className="p-6 bg-gradient-to-r from-purple-500/10 to-primary/10 border-primary bg-card/50 backdrop-blur-sm">
          <h3 className="text-lg font-semibold mb-4">{t('settings.profile.upgrade')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('settings.profile.upgrade_desc')}
          </p>
          <Button onClick={() => navigate("/upgrade")} className="bg-gradient-to-r from-purple-600 to-primary">
            <Crown className="w-4 h-4 mr-2" />
            {t('settings.profile.upgrade')}
          </Button>
        </Card>
      )}

      {isPremium && showOnlyAccount && (
        <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
          <h3 className="text-lg font-semibold mb-4">{t('settings.account.subscription_management')}</h3>
          {isLifetime || subscriptionProductId === 'lifetime' ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-purple-500" />
                <p className="text-sm font-medium text-purple-500">{t('settings.account.lifetime_premium')}</p>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {t('settings.account.lifetime_desc')}
              </p>
            </>
          ) : subscriptionProductId?.startsWith('revenuecat_') ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <img src="/apple.png" alt="Apple" className="w-5 h-5" />
                <p className="text-sm font-medium">{t('settings.account.apple_subscription')}</p>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {t('settings.account.apple_desc')}
              </p>
            </>
          ) : subscriptionStatus?.cancel_at_period_end ? (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                {t('settings.account.cancel_scheduled', { date: subscriptionStatus?.cancel_at ? new Date(subscriptionStatus.cancel_at * 1000).toLocaleDateString() : 'the end of your billing period' })}
              </p>
              <Button variant="default" onClick={handleUncancelSubscription} disabled={isUncanceling}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {isUncanceling ? t('settings.account.reactivating') : t('settings.account.reactivate')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                {t('settings.account.cancel_desc', { date: subscriptionEnd ? new Date(subscriptionEnd).toLocaleDateString() : 'the end of your billing period' })}
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={isCanceling}>
                    <XCircle className="w-4 h-4 mr-2" />
                    {isCanceling ? t('settings.account.canceling') : t('settings.account.cancel_button')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('settings.account.confirm_cancel_title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('settings.account.confirm_cancel_desc')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('settings.account.keep_subscription')}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancelSubscription}>
                      {isCanceling && <MiniGridLoader className="w-4 h-4 mr-2" />}
                      {t('settings.account.yes_cancel')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </Card>
      )}

      {showOnlyAccount && (
        <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
          <h3 className="text-lg font-semibold mb-4">{t('settings.account.export_data')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('settings.account.export_data_desc')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={exportData}
              disabled={isExporting}
              variant="outline"
            >
              {isExporting ? (
                <MiniGridLoader className="w-4 h-4 mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isExporting ? t('settings.account.exporting') : t('settings.account.export_json')}
            </Button>
            <Button
              onClick={saveAllProgressPhotos}
              disabled={isSavingPhotos}
              variant="outline"
            >
              {isSavingPhotos ? (
                <MiniGridLoader className="w-4 h-4 mr-2" />
              ) : (
                <ImageDown className="w-4 h-4 mr-2" />
              )}
              {isSavingPhotos
                ? photoProgress.phase === "downloading"
                  ? `${t('settings.account.downloading_photos')} (${photoProgress.current}/${photoProgress.total})`
                  : `${t('settings.account.saving_photos')} (${photoProgress.current}/${photoProgress.total})`
                : t('settings.account.save_all_photos')}
            </Button>
          </div>
        </Card>
      )}

      {showOnlyAccount && (
        <Card className="p-6 border-destructive bg-card/50 backdrop-blur-sm">
          <h3 className="text-lg font-semibold mb-4 text-destructive">{t('settings.account.danger_zone')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('settings.account.delete_desc')}
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isDeleting}>
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? t('settings.account.deleting') : t('settings.account.delete_button')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('settings.account.delete_confirm_title')}</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>{t('settings.account.delete_confirm_desc')}</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>{t('settings.account.delete_list_1')}</li>
                    <li>{t('settings.account.delete_list_2')}</li>
                    <li>{t('settings.account.delete_list_3')}</li>
                    <li>{t('settings.account.delete_list_4')}</li>
                  </ul>
                  <p className="font-semibold mt-2">{t('settings.account.delete_confirm_final')}</p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('dashboard.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  {isDeleting && <MiniGridLoader className="w-4 h-4 mr-2" />}
                  {t('settings.account.yes_delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>
      )}

      {showOnlyAccount && (
        <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
          <h3 className="text-lg font-semibold mb-4">{t('settings.account.private_id')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('settings.account.private_id_desc')}
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="password"
              value={profile?.private_account_id || t('settings.loading')}
              readOnly
              className="font-mono text-sm bg-muted"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (profile?.private_account_id) {
                  navigator.clipboard.writeText(profile.private_account_id);
                  toast.success(t('settings.account.id_copied'));
                }
              }}
              disabled={!profile?.private_account_id}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {showOnlyAccount && (
        <Collapsible defaultOpen={false}>
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <Ban className="w-5 h-5" />
                <div className="text-left">
                  <h3 className="text-lg font-semibold">{t('settings.account.blocked_users')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.account.blocked_users_desc')}
                  </p>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-6 pb-6">
              <BlockedUsersList userId={userId} />
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
};

export default AccountSettings;
